import React from 'react';
import { SectionInstance } from '../types';
import InlineEditText from '../InlineEditText';
import { getf, sfHeadline, sfSub } from './sectionHelpers';

interface Props {
  section: SectionInstance;
}

const CaseStudyCards: React.FC<Props> = ({ section }) => {
  const c = section.controls;

  // Support: cases, studies, items as array key aliases
  const rawCases = (c.cases || c.studies || c.items || []) as Array<Record<string, any>>;

  // Normalise each case: support both metrics array AND Python flat fields (result_metric / result_value)
  const cases = rawCases.map(s => {
    let metrics: Array<{ label: string; value: string }> = [];
    if (Array.isArray(s.metrics) && s.metrics.length > 0) {
      metrics = s.metrics;
    } else if (s.result_metric || s.result_value) {
      // Python flat format: one metric per card
      metrics = [{ label: s.result_metric || 'Result', value: s.result_value || '' }];
    }
    return {
      title:       getf(s, 'title', 'heading', 'name') || 'Case Study',
      client:      getf(s, 'client', 'company', 'brand') || '',
      image_url:   getf(s, 'image_url', 'image', 'background_image') || '',
      description: getf(s, 'description', 'body', 'summary') || '',
      metrics,
      url:         getf(s, 'url', 'cta_url', 'link') || '#',
      cta_label:   getf(s, 'cta_label', 'cta_text', 'link_text') || 'Read Case Study',
    };
  });

  const DEFAULT_CASES = cases.length > 0 ? cases : [
    {
      title: 'How We Tripled Revenue', client: 'Acme Corp', image_url: '',
      description: 'A comprehensive digital strategy that transformed the business.',
      metrics: [{ label: 'Revenue Growth', value: '+312%' }, { label: 'Leads Generated', value: '4,200' }, { label: 'Time to Results', value: '90 days' }],
      url: '#', cta_label: 'Read Case Study',
    },
    {
      title: 'Enterprise SEO Overhaul', client: 'GlobalTech', image_url: '',
      description: 'Dominating search rankings in a competitive market.',
      metrics: [{ label: 'Organic Traffic', value: '+180%' }, { label: 'Keywords Ranked', value: '1,200+' }, { label: 'ROI', value: '8x' }],
      url: '#', cta_label: 'Read Case Study',
    },
  ];

  return (
    <section className="sb-render sb-render-case-study-cards" style={{ padding: '80px 24px', background: 'var(--brand-bg-alt, #f8fafc)' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 56 }}>
          <InlineEditText
            tag="h2"
            controlKey="headline"
            style={{ fontSize: '2.5rem', fontWeight: 700, marginBottom: 12, letterSpacing: '-0.025em' }}
          >
            {sfHeadline(c, 'Case Studies')}
          </InlineEditText>
          <InlineEditText
            tag="p"
            controlKey="subheadline"
            style={{ fontSize: '1.125rem', color: 'var(--brand-text-light, #64748b)', maxWidth: 560, margin: '0 auto' }}
          >
            {sfSub(c, 'Real results from real clients.')}
          </InlineEditText>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(480px, 1fr))', gap: 32 }}>
          {DEFAULT_CASES.map((study, i) => (
            <div
              key={i}
              style={{
                background: 'var(--brand-bg, #fff)',
                borderRadius: 'var(--brand-card-radius, 16px)',
                overflow: 'hidden',
                boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
                border: '1px solid rgba(0,0,0,0.06)',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {/* Image / client banner */}
              <div style={{
                height: 200,
                background: study.image_url
                  ? `url(${study.image_url}) center/cover no-repeat`
                  : `linear-gradient(${135 + i * 30}deg, var(--brand-primary, #2563eb), var(--brand-secondary, #7c3aed))`,
                position: 'relative',
                display: 'flex',
                alignItems: 'flex-end',
                padding: '16px 24px',
              }}>
                <div style={{
                  position: 'absolute', inset: 0,
                  background: 'linear-gradient(to top, rgba(0,0,0,0.6) 0%, transparent 60%)',
                }} />
                <span style={{
                  position: 'relative', zIndex: 1,
                  background: 'rgba(255,255,255,0.15)',
                  backdropFilter: 'blur(8px)',
                  border: '1px solid rgba(255,255,255,0.25)',
                  color: '#fff',
                  padding: '4px 12px',
                  borderRadius: 9999,
                  fontSize: '0.8rem',
                  fontWeight: 600,
                }}>
                  {study.client}
                </span>
              </div>

              {/* Content */}
              <div style={{ padding: '28px 28px 20px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                <h3 style={{ fontSize: '1.3rem', fontWeight: 700, marginBottom: 10, lineHeight: 1.3 }}>{study.title}</h3>
                <p style={{ color: 'var(--brand-text-light, #64748b)', lineHeight: 1.65, marginBottom: 24, fontSize: '0.95rem' }}>{study.description}</p>

                {/* Metrics */}
                {study.metrics && study.metrics.length > 0 && (
                  <div style={{
                    display: 'flex',
                    gap: 0,
                    borderTop: '1px solid rgba(0,0,0,0.07)',
                    borderBottom: '1px solid rgba(0,0,0,0.07)',
                    margin: '0 -28px 20px',
                    padding: '0 28px',
                  }}>
                    {study.metrics.map((metric, mi) => (
                      <div key={mi} style={{
                        flex: 1,
                        padding: '16px 8px',
                        textAlign: 'center',
                        borderRight: mi < study.metrics.length - 1 ? '1px solid rgba(0,0,0,0.07)' : 'none',
                      }}>
                        <div style={{
                          fontSize: '1.5rem', fontWeight: 800,
                          color: 'var(--brand-primary, #2563eb)',
                          lineHeight: 1,
                          marginBottom: 4,
                        }}>
                          {metric.value}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--brand-text-light, #64748b)', fontWeight: 500 }}>
                          {metric.label}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* CTA */}
                <a
                  href={study.url || '#'}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    color: 'var(--brand-primary, #2563eb)',
                    fontWeight: 600, textDecoration: 'none',
                    fontSize: '0.9rem',
                    marginTop: 'auto',
                    transition: 'gap 0.2s',
                  }}
                >
                  {study.cta_label} <span>→</span>
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CaseStudyCards;
